import 'regenerator-runtime/runtime';

import React, { Component } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { fromUnixTime, format } from 'date-fns';
import piexif from 'piexifjs';
import { tokens } from '@companycam/slab-tokens';

import { loadImageFromSrc, piexifCopyExif } from '../utils/image';
import ModuleWrapper from './ModuleWrapper';
import { ButtonPrimary } from './buttons/Button';
import IconButton from './buttons/IconButton';
import CloseButton from './buttons/CloseButton';
import ConfirmExit from './ConfirmExit';
import SaveButton from './buttons/SaveButton';
import TextInput from './TextInput';
import SidePanelCrop from './SidePanelCrop';
import SidePanelColor from './SidePanelColor';
import SidePanelColorAdjust from './SidePanelColorAdjust';
import SidePanelStickers from './SidePanelStickers';
import SidePanelThickness from './SidePanelThickness';
import LoadingIndicator from './LoadingIndicator';

import { COLOR_YELLOW } from '../constants/colors';
import { THICKNESS_NORMAL, THICKNESS_ICONS } from '../constants/thicknesses';
import { FILL_MODE_NONE } from '../constants/fillModes';
import { ResourcePropType } from '../constants/propTypes';
import {
  mdiArrowTopRight,
  mdiCircleOutline,
  mdiClockOutline,
  mdiCrop,
  mdiCursorDefault,
  mdiFormatText,
  mdiGesture,
  mdiRotateLeft,
  mdiStickerCircleOutline,
  mdiSquareOutline,
  mdiSquareRounded,
  mdiVectorPolyline,
  mdiTune,
  mdiUndo,
  mdiRedo,
  mdiRestore,
} from '@mdi/js';

const BACKGROUND_COLOR = { rgb: [33, 33, 33] };
const INITIAL_COLOR = COLOR_YELLOW;
const INITIAL_THICKNESS = THICKNESS_NORMAL;
const INITIAL_HIGHLIGHTER_MODE = false;
const INITIAL_FILL_MODE = FILL_MODE_NONE;

const OVERLAY_NONE = 'OVERLAY_NONE';
const SIDEPANEL_CROP = 'SIDEBAR_CROP';
const SIDEPANEL_COLOR = 'SIDEPANEL_COLOR';
const SIDEPANEL_COLOR_ADJUST = 'SIDEPANEL_COLOR_ADJUST';
const SIDEPANEL_STICKERS = 'SIDEPANEL_STICKERS';
const SIDEPANEL_THICKNESS = 'SIDEPANEL_THICKNESS';
const OVERLAY_TEXTINPUT_NEW = 'OVERLAY_TEXTINPUT_NEW';
const OVERLAY_TEXTINPUT_EDIT = 'OVERLAY_TEXTINPUT_EDIT';
const OVERLAY_CONFIRM_EXIT = 'OVERLAY_CONFIRM_EXIT';

const Wrapper = styled.div`
  width: 100%;
  height: 100%;
  background-color: ${tokens.color_label_dark_gray};
  box-sizing: border-box;
`;

const ContentsWrapper = styled.div`
  display: flex;
  width: 100%;
  height: 100%;
  overflow: auto;
  background: white;
`;

const CloseContainer = styled.div`
  position: absolute;
  top: 8px;
  right: 16px;
  z-index: 1000;
`;

const Toolbar = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  align-items: center;
  padding: 8px 0;
  overflow: visible;
`;

const UndoRedoDiv = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-top: auto;
  padding: 8px;
`;

const SaveDiv = styled.div`
  display: flex;
  align-items: center;
  height: 64px;
`;

const LeftToolbarContents = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: center;
`;

const CanvasWrapper = styled.div`
  z-index: 100;
  pointer-events: none;
  flex-grow: 1;
  height: 100%;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  background-color: rgba(
    ${BACKGROUND_COLOR.rgb[0]},
    ${BACKGROUND_COLOR.rgb[1]},
    ${BACKGROUND_COLOR.rgb[2]},
    ${(props) => (props.opaque ? 1.0 : 0.0)}
  );
`;

const SpacingSpan = styled.span`
  background-color: ${tokens.color_background_3_le};
  height: 1px;
  width: 100%;
  margin: 8px 0;
`;

const stopEventPropagation = (event) => {
  if (
    document.activeElement &&
    document.activeElement.className.includes('CCEditorTextInput')
  ) {
    event.stopImmediatePropagation();
  }
};

class CCEditor extends Component {
  constructor(props) {
    super(props);

    this.state = {
      exifOriginal: {},
      sketchMode: false,
      polylineSelected: false,
      currentColor: INITIAL_COLOR,
      currentThickness: INITIAL_THICKNESS,
      loading: true,
      overlay: OVERLAY_NONE,
      canUndo: false,
      canRedo: false,
      textInputInitialLines: [''],
    };

    this.refCanvasWrapper = React.createRef();
    this.refColorAdjust = React.createRef();
    this.moduleInitializer = null;
    this.moduleMethods = null;
  }

  async componentDidMount() {
    if (!this.moduleInitializer) {
      return;
    }

    // NOTE: Emscripten steals certain key input (e.g. backspace) by default.
    // These will stop it--they must be added before the module initializes.
    window.addEventListener('keydown', stopEventPropagation, true);
    window.addEventListener('keyup', stopEventPropagation, true);

    const initialCanvasRect = this.getCanvasRect();
    this.moduleMethods = await this.moduleInitializer({
      initialCanvasRect,
      logger: (...args) => this.props.logger('[EMSCRIPTEN]', ...args),
    });
    window.addEventListener('resize', this.onResize);

    // NOTE: (initial dims)*(pixel ratio) is passed into:
    // - glfwCreateWindow in the module constructor (via arguments)
    // - glfwSetWindowSize on resize (via setCanvasSize)
    // For some reason glfwCreateWindow appears to divide the pixel ratio back
    // out, whereas glfwSetWindowSize still works correctly.
    // It seems emsdk's library_glfw.js may have changed DPI handling.
    // For now, simply triggering glfwSetWindowSize here fixes the issue.
    this.moduleMethods.setCanvasRect(initialCanvasRect);

    this.moduleMethods.setColorBG(BACKGROUND_COLOR.rgb);
    this.moduleMethods.setActiveColor(INITIAL_COLOR.rgb, false);
    this.moduleMethods.setActiveThickness(INITIAL_THICKNESS.value, false);
    this.moduleMethods.setActiveHighlighterMode(
      INITIAL_HIGHLIGHTER_MODE,
      false,
    );
    this.moduleMethods.setActiveFillMode(INITIAL_FILL_MODE.value, false);

    const { imgData, dataURL } = await loadImageFromSrc({
      src: this.props.imageURL,
      returnImageData: true,
      returnDataURL: true,
    });
    const exifOriginal = piexif.load(dataURL);

    await this.moduleMethods.setImageData(imgData);
    this.moduleMethods.loadJSON(this.props.initialJSON);

    this.setState({ loading: false, exifOriginal });
  }

  componentDidUpdate(prevProps, prevState) {
    const prevCropping = prevState.overlay === SIDEPANEL_CROP;
    const nextCropping = this.state.overlay === SIDEPANEL_CROP;

    if (prevCropping && !nextCropping) {
      this.moduleMethods.cropStopEditing();
    }
    if (!prevCropping && nextCropping) {
      this.moduleMethods.cropEnableAndEdit();
    }
  }

  componentWillUnmount() {
    window.removeEventListener('keydown', stopEventPropagation, true);
    window.removeEventListener('keyup', stopEventPropagation, true);

    window.removeEventListener('resize', this.onResize);
  }

  getCanvasRect = () => {
    const divwrap = this.refCanvasWrapper.current;
    const { width, height, left, top } = divwrap.getBoundingClientRect();
    return { width, height, left, top };
  };

  onResize = () => {
    if (!this.moduleMethods || !this.refCanvasWrapper.current) {
      return;
    }

    this.moduleMethods.setCanvasRect(this.getCanvasRect());
  };

  setSketchMode(val) {
    this.setState({ sketchMode: val });
    this.moduleMethods.setSketchModeEnabled(val);
  }

  moduleOnTextButtonPressed = () => {
    const lines = this.moduleMethods.getSelectedText();

    this.setState({
      overlay: OVERLAY_TEXTINPUT_EDIT,
      textInputInitialLines: lines,
    });
  };

  moduleOnSelectionChanged = () => {
    this.setState({
      polylineSelected: this.moduleMethods.polylineIsSelected(),
    });
  };

  moduleOnHistoryChanged = (canUndo, canRedo) => {
    this.setState({ canUndo: !!canUndo, canRedo: !!canRedo });
  };

  onSaveButtonPressed = async () => {
    const { jpgQuality } = this.props;
    const { exifOriginal } = this.state;

    const json = this.moduleMethods.saveJSON();
    const {
      width,
      height,
      dataURL,
    } = await this.moduleMethods.getOutputImageDataURL({
      jpgQuality,
    });

    const exifFinal = piexifCopyExif({ exifOriginal, width, height });
    const exifFinalBytes = piexif.dump(exifFinal);
    const dataURLFinal = piexif.insert(exifFinalBytes, dataURL);

    this.props.onSave({ dataURL: dataURLFinal, json });
  };

  addElement(type, lines = null) {
    this.moduleMethods.addElement(type, lines);
    this.setSketchMode(false);
  }

  toggleOverlay(type) {
    if (this.state.overlay === type) {
      this.setState({ overlay: OVERLAY_NONE });
    } else {
      this.setState({ overlay: type });
    }
  }

  isPolylineMode() {
    return !this.state.sketchMode && this.state.polylineSelected;
  }

  shouldShowSaveContainer() {
    return (
      !this.state.sketchMode &&
      !this.state.polylineSelected &&
      this.state.overlay === OVERLAY_NONE
    );
  }

  renderDoneButton() {
    return (
      <ButtonPrimary
        size="tiny"
        onClick={() => {
          this.setState({ overlay: OVERLAY_NONE });
          this.setSketchMode(false);
          this.moduleMethods.deselect();
        }}
      >
        Done
      </ButtonPrimary>
    );
  }

  renderUndoRedo() {
    return (
      <UndoRedoDiv>
        <IconButton
          icon={mdiRestore}
          disabled={false}
          label="Clear All"
          onClick={() => {
            this.moduleMethods.clearAll();
          }}
        />
        <IconButton
          icon={mdiUndo}
          disabled={!this.state.canUndo}
          label="Undo"
          onClick={() => {
            this.moduleMethods.undo();
            if (this.refColorAdjust.current) {
              this.refColorAdjust.current.update();
            }
          }}
        />
        <IconButton
          icon={mdiRedo}
          disabled={!this.state.canRedo}
          label="Redo"
          onClick={() => {
            this.moduleMethods.redo();
            if (this.refColorAdjust.current) {
              this.refColorAdjust.current.update();
            }
          }}
        />
      </UndoRedoDiv>
    );
  }

  renderElementStyleButtons() {
    return (
      <>
        <IconButton
          selected={this.state.overlay === SIDEPANEL_COLOR}
          icon={mdiSquareRounded}
          label="Color"
          addStroke
          onClick={() => {
            this.toggleOverlay(SIDEPANEL_COLOR);
          }}
          rgb={this.state.currentColor.rgb}
        />
        <IconButton
          selected={this.state.overlay === SIDEPANEL_THICKNESS}
          icon={THICKNESS_ICONS[this.state.currentThickness.key]}
          label="Thickness"
          onClick={() => {
            this.toggleOverlay(SIDEPANEL_THICKNESS);
          }}
        />
      </>
    );
  }

  renderSubToolbar() {
    return (
      <LeftToolbarContents>
        {this.renderDoneButton()}
        <SpacingSpan />
        {this.isPolylineMode() ? (
          <IconButton
            selected
            icon={mdiVectorPolyline}
            label="Line"
            onClick={() => {}}
          />
        ) : (
          <IconButton
            selected={this.state.sketchMode}
            icon={mdiGesture}
            label="Draw"
            onClick={() => {}}
          />
        )}
        <SpacingSpan />
        {this.renderElementStyleButtons()}
        <SpacingSpan />
        {this.renderUndoRedo()}
      </LeftToolbarContents>
    );
  }

  renderLeftToolbar() {
    if (this.isPolylineMode() || this.state.sketchMode) {
      return this.renderSubToolbar();
    }

    return (
      <LeftToolbarContents>
        <IconButton
          icon={mdiCursorDefault}
          selected={!this.state.sketchMode && !this.state.polylineSelected}
          label="Select"
          onClick={() => {
            this.setState({ overlay: OVERLAY_NONE });
            if (this.state.sketchMode) {
              this.setSketchMode(false);
            }
            if (this.state.polylineSelected) {
              this.moduleMethods.deselect();
            }
          }}
        />
        <IconButton
          selected={this.state.sketchMode}
          icon={mdiGesture}
          label="Draw"
          onClick={() => {
            this.setState({ overlay: OVERLAY_NONE });
            this.setSketchMode(!this.state.sketchMode);
            this.moduleMethods.deselect();
          }}
        />
        <IconButton
          selected={this.isPolylineMode()}
          icon={mdiVectorPolyline}
          label="Line"
          onClick={() => {
            this.setState({ overlay: OVERLAY_NONE });
            if (this.isPolylineMode()) {
              this.moduleMethods.deselect();
            } else {
              this.addElement('POLYLINE');
            }
          }}
        />
        <SpacingSpan />
        {this.renderElementStyleButtons()}
        <SpacingSpan />
        <IconButton
          icon={mdiArrowTopRight}
          label="Arrow"
          onClick={() => {
            this.setState({ overlay: OVERLAY_NONE });
            this.addElement('ARROW');
          }}
        />
        <IconButton
          icon={mdiCircleOutline}
          label="Circle"
          onClick={() => {
            this.setState({ overlay: OVERLAY_NONE });
            this.addElement('CIRCLE');
          }}
        />
        <IconButton
          icon={mdiSquareOutline}
          label="Square"
          onClick={() => {
            this.setState({ overlay: OVERLAY_NONE });
            this.addElement('QUAD');
          }}
        />
        <IconButton
          icon={mdiFormatText}
          selected={this.state.overlay === OVERLAY_TEXTINPUT_NEW}
          label="Text"
          onClick={() => {
            this.setState({
              overlay: OVERLAY_TEXTINPUT_NEW,
              textInputInitialLines: [''],
            });
          }}
        />
        <SpacingSpan />
        <IconButton
          icon={mdiClockOutline}
          label="Timestamp"
          onClick={() => {
            this.setState({ overlay: OVERLAY_NONE });
            const d = fromUnixTime(this.props.imageTimeUnixS);
            const lines = ['MMM d yyyy', 'h:mm aaaa'].map((f) => format(d, f));
            this.addElement('TEXT', lines);
          }}
        />
        <IconButton
          icon={mdiStickerCircleOutline}
          selected={this.state.overlay === SIDEPANEL_STICKERS}
          label="Sticker"
          onClick={() => {
            this.toggleOverlay(SIDEPANEL_STICKERS);
          }}
        />
        <SpacingSpan />
        <IconButton
          icon={mdiRotateLeft}
          label="Rotate"
          onClick={() => {
            this.moduleMethods.rotateImage();
          }}
        />
        <IconButton
          selected={this.state.overlay === SIDEPANEL_CROP}
          icon={mdiCrop}
          label="Crop"
          onClick={() => {
            this.toggleOverlay(SIDEPANEL_CROP);
          }}
        />
        <IconButton
          selected={this.state.overlay === SIDEPANEL_COLOR_ADJUST}
          icon={mdiTune}
          label="Edit"
          onClick={() => {
            this.toggleOverlay(SIDEPANEL_COLOR_ADJUST);
          }}
        />
        <SpacingSpan />
        {this.renderUndoRedo()}
      </LeftToolbarContents>
    );
  }

  renderOverlay() {
    switch (this.state.overlay) {
      case SIDEPANEL_CROP:
        return (
          <SidePanelCrop
            onDelete={() => {
              this.setState({ overlay: OVERLAY_NONE });
              this.moduleMethods.cropDisableAndReset();
            }}
            onClose={() => {
              this.setState({ overlay: OVERLAY_NONE });
            }}
          />
        );
      case SIDEPANEL_COLOR:
        return (
          <SidePanelColor
            currentColorKey={this.state.currentColor.key}
            onSelectColorOption={(option) => {
              this.moduleMethods.setActiveColor(option.rgb, true);
              this.setState({ currentColor: option });
            }}
            onClose={() => {
              this.setState({ overlay: OVERLAY_NONE });
            }}
          />
        );
      case SIDEPANEL_COLOR_ADJUST:
        return (
          <SidePanelColorAdjust
            ref={this.refColorAdjust}
            getColorAdjust={this.moduleMethods.getColorAdjust}
            setColorAdjust={this.moduleMethods.setColorAdjust}
            onClose={() => {
              this.setState({ overlay: OVERLAY_NONE });
            }}
          />
        );
      case SIDEPANEL_STICKERS:
        return (
          <SidePanelStickers
            onClose={() => {
              this.setState({ overlay: OVERLAY_NONE });
            }}
          >
            {this.props.renderStickerChoices({
              onSelectSticker: (sticker) => {
                this.moduleMethods.addSticker(sticker.key, sticker.src);
                this.setSketchMode(false);
              },
            })}
          </SidePanelStickers>
        );
      case SIDEPANEL_THICKNESS:
        return (
          <SidePanelThickness
            currentThicknessKey={this.state.currentThickness.key}
            onSelectThicknessOption={(option) => {
              this.moduleMethods.setActiveThickness(option.value, true);
              this.setState({ currentThickness: option });
            }}
            onClose={() => {
              this.setState({ overlay: OVERLAY_NONE });
            }}
          />
        );
      case OVERLAY_TEXTINPUT_NEW:
      case OVERLAY_TEXTINPUT_EDIT:
        return (
          <TextInput
            initialLines={this.state.textInputInitialLines}
            onCancel={() => {
              this.setState({ overlay: OVERLAY_NONE });
            }}
            onSave={(lines) => {
              if (this.state.overlay === OVERLAY_TEXTINPUT_NEW) {
                this.moduleMethods.addElement('TEXT', lines);
                this.setSketchMode(false);
              } else if (this.state.overlay === OVERLAY_TEXTINPUT_EDIT) {
                this.moduleMethods.setSelectedText(lines);
              }
            }}
          />
        );
      case OVERLAY_CONFIRM_EXIT:
        return (
          <ConfirmExit
            onClickPrimary={this.props.onCancel}
            onClickCancel={() => {
              this.setState({ overlay: OVERLAY_NONE });
            }}
          />
        );
      default:
        return null;
    }
  }

  render() {
    return (
      <Wrapper>
        <ContentsWrapper>
          {this.shouldShowSaveContainer() ? (
            <CloseContainer>
              <SaveDiv>
                {!!this.state.canUndo && (
                  <SaveButton onClick={this.onSaveButtonPressed} />
                )}
                <CloseButton
                  onClick={() => {
                    if (!this.state.canUndo) {
                      this.props.onCancel();
                    } else {
                      this.setState({ overlay: OVERLAY_CONFIRM_EXIT });
                    }
                  }}
                />
              </SaveDiv>
            </CloseContainer>
          ) : null}
          <Toolbar>{this.renderLeftToolbar()}</Toolbar>
          <CanvasWrapper
            opaque={this.state.loading}
            ref={this.refCanvasWrapper}
          >
            {this.renderOverlay()}
          </CanvasWrapper>
          <ModuleWrapper
            stickerKeyURLs={this.props.stickerKeyURLs}
            onTextButtonPressed={this.moduleOnTextButtonPressed}
            onSelectionChanged={this.moduleOnSelectionChanged}
            onHistoryChanged={this.moduleOnHistoryChanged}
            setInitializer={(initialize) => {
              this.moduleInitializer = initialize;
            }}
          />
        </ContentsWrapper>
        {!!this.state.loading && <LoadingIndicator />}
      </Wrapper>
    );
  }
}

CCEditor.propTypes = {
  imageURL: PropTypes.string.isRequired,
  initialJSON: PropTypes.string,
  imageTimeUnixS: PropTypes.number,
  stickerKeyURLs: PropTypes.arrayOf(ResourcePropType),
  renderStickerChoices: PropTypes.func.isRequired,
  jpgQuality: PropTypes.number,
  logger: PropTypes.func,
  onCancel: PropTypes.func,
  onSave: PropTypes.func,
};

CCEditor.defaultProps = {
  initialJSON: '{}',
  imageTimeUnixS: Math.round(0.001 * Date.now()),
  stickerKeyURLs: [],
  jpgQuality: 0.8,
  logger: () => {}, // (...strings) => void
  onCancel: () => {}, // () => void
  onSave: () => {}, // ({ dataURL, json }) => void
};

export default CCEditor;
